import { updatePaginationState } from "@/store/paginationStore";
import { Button } from "@/components/ui/button";
import { RefreshCcwIcon } from "lucide-react";
import { PaginatedVacationBonusQueryVariables, VacationBonusFilters } from "@/graphql/__generated__/graphql";
import { VacationBonusDateRange, VacationBonusSortingDropdown, VacationBonusValueRange, VacationBonusProvincesDropdown } from "./Components";

interface Props {
  onFilterSubmit: (_variables: PaginatedVacationBonusQueryVariables) => void;
  displayAmount: number;
}

/**
 * VacationBonusFilter component handles filtering and sorting of vacation bonus
 * It manages filter state and provides UI for filter controls
 * @param props Component properties
 * @returns JSX element
 */
const VacationBonusFilter = (props: Props) => {
  const { onFilterSubmit, displayAmount } = props;

  /**
   * Handles filter submission
   * @param newValues New filter values to submit
   */
  const handleSubmitClick = (newValues: PaginatedVacationBonusQueryVariables) => {
    onFilterSubmit(newValues);
  };

  /**
   * Resets all filter values and sort settings
   * Updates both local state and triggers query update
   */
  const handleResetClickFilters = () => {
    // Define empty filters object
    const emptyFilters = {
      activeFrom: null,
      bonusRelative: null,
      bonusAbsolute: null,
      provinces: null,
    };

    // Reset all filter values and sort in the global store
    updatePaginationState({
      paginatedVacationBonus: {
        filters: emptyFilters as VacationBonusFilters,
        sort: null
      }
    });

    // Create reset query variables with explicitly empty filters and sort
    const updatedVariables: PaginatedVacationBonusQueryVariables = {
      first: displayAmount,
      filters: {
        activeFrom: null,
        bonusRelative: null,
        bonusAbsolute: null,
        provinces: null,
      },
      sort: null
    };

    // Immediately submit the reset filter state to parent component
    onFilterSubmit(updatedVariables);
  };

  return (
    <div className="flex flex-1 gap-2">
      <div className="flex flex-1 items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Province Multi-Select - Controls province filter */}
          <VacationBonusProvincesDropdown
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />

          {/* Date Range - Controls date filter */}
          <VacationBonusDateRange
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />

          {/* Value Search - Controls value filter */}
          <VacationBonusValueRange
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />

          {/* Sorting Dropdown - Controls sort field and direction */}
          <VacationBonusSortingDropdown
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />
        </div>
        <div>
          <Button variant="outline" size="icon" onClick={handleResetClickFilters}>
            <RefreshCcwIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VacationBonusFilter;
